// File: client/src/platform/rulebookContract.js
//
// THE RULEBOOK CONTRACT — a structured, data-only description of a variant's rules.
// The RulebookModal renders whatever a variant provides through this shape; no
// variant ships its own rulebook UI. Rulebooks are the canonical documentation and
// must be kept in sync with the implementation.
//
// Shape (all section fields optional except overview/objective; render order fixed):
//
//   overview        {string}          what the variant is, in a sentence or two.
//   objective       {string}          how you win, stated plainly.
//   setup           {string}          starting position / deck / etc. (if non-standard).
//   turnFlow        {string[]}         ordered steps of a turn.
//   mechanics       {Section[]}        the unique systems (cards, shrinking, zombies…).
//   specialPieces   {Section[]}        pieces that behave non-standardly.
//   winConditions   {string[]}         precise win/draw/loss rules.
//   tips            {string[]}         strategy pointers.
//   knownInteractions {string[]}       edge cases / documented rule decisions.
//
//   A Section is { title: string, body: string } for a labelled block, or
//   { title: string, items: string[] } for a labelled bullet list.
//
// Where a rule in the design brief was ambiguous, the variant SHOULD implement a
// sensible rule and document the decision under knownInteractions (per the brief:
// "design a rule that preserves the spirit of the game … document the decision").

/**
 * Validate the minimal required rulebook fields. Throws so a variant author gets a
 * clear error at registration rather than a blank modal at runtime.
 * @param {object} rb
 * @param {string} variantId for the error message
 * @returns {object} the rulebook (unchanged) if valid
 */
export function validateRulebook(rb, variantId = '?') {
  if (!rb || typeof rb !== 'object') {
    throw new Error(`rulebook for "${variantId}" must be an object`);
  }
  if (typeof rb.overview !== 'string' || !rb.overview) {
    throw new Error(`rulebook for "${variantId}" requires an overview`);
  }
  if (typeof rb.objective !== 'string' || !rb.objective) {
    throw new Error(`rulebook for "${variantId}" requires an objective`);
  }
  return rb;
}

/** The section order the renderer uses (kept here so it's documented in one place). */
export const RULEBOOK_SECTIONS = [
  { key: 'overview', label: 'Overview', type: 'text' },
  { key: 'objective', label: 'Objective', type: 'text' },
  { key: 'setup', label: 'Setup', type: 'text' },
  { key: 'turnFlow', label: 'Turn Flow', type: 'orderedList' },
  { key: 'mechanics', label: 'Mechanics', type: 'sections' },
  { key: 'specialPieces', label: 'Special Pieces', type: 'sections' },
  { key: 'winConditions', label: 'Winning & Losing', type: 'list' },
  { key: 'tips', label: 'Tips', type: 'list' },
  { key: 'knownInteractions', label: 'Known Interactions & Rule Decisions', type: 'list' },
];
